import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import {
  fieldProblems,
  financeError,
  useExpected,
  useInvoiceReminderSettings,
  useSaveInvoiceReminderSettings,
  useSetExpected,
} from '../../hooks/useFinance';
import { CARD, INPUT, PRIMARY, SECONDARY, formatDateTime } from './financeText';

const MAX_DAYS = 5;

// Days from the 15th on remind about this month; days before it about
// last month, and the last of those is the final reminder.
const ordinal = (n) => {
  const rule = new Intl.PluralRules('en', { type: 'ordinal' });
  const suffix = { one: 'st', two: 'nd', few: 'rd', other: 'th' }[
    rule.select(n)
  ];
  return `${n}${suffix}`;
};

export const describeDays = (days) => {
  const early = [...days].filter((d) => d < 15).sort((a, b) => a - b);
  const final = early.length ? early[early.length - 1] : null;
  // Calendar order through the reminder cycle: this month's days, then next.
  return [
    ...[...days].filter((d) => d >= 15).sort((a, b) => a - b),
    ...early,
  ].map((d) => {
    let role = 'last month';
    if (d >= 15) role = 'this month';
    else if (d === final) role = 'final reminder';
    return { day: d, role };
  });
};

function DaysEditor({ days, onChange, error }) {
  const [input, setInput] = useState('');
  const value = Number(input);
  const valid =
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 28 &&
    !days.includes(value);
  const add = () => {
    if (!valid || days.length >= MAX_DAYS) return;
    onChange([...days, value]);
    setInput('');
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {describeDays(days).map(({ day, role }) => (
          <span
            key={day}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800"
          >
            {ordinal(day)}
            <span className="text-[11px] text-gray-500">{role}</span>
            {days.length > 1 && (
              <button
                type="button"
                aria-label={`Remove the ${ordinal(day)}`}
                onClick={() => onChange(days.filter((d) => d !== day))}
                className="text-gray-400 hover:text-gray-700"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            )}
          </span>
        ))}
        {days.length < MAX_DAYS && (
          <span className="inline-flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={28}
              aria-label="Add a reminder day"
              placeholder="Day"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              className={`${INPUT} w-20`}
            />
            <button
              type="button"
              onClick={add}
              disabled={!valid}
              className={SECONDARY}
            >
              Add
            </button>
          </span>
        )}
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}

function Settings() {
  const { data, isLoading, error } = useInvoiceReminderSettings();
  const save = useSaveInvoiceReminderSettings();
  const [days, setDays] = useState([]);
  const [problem, setProblem] = useState(null);
  useEffect(() => {
    if (data?.days) setDays(data.days);
  }, [data]);

  if (isLoading)
    return (
      <div className={`${CARD} h-40 animate-pulse`} aria-label="Loading" />
    );
  if (error) {
    return (
      <div className={`${CARD} p-6 text-sm text-gray-600`}>
        {financeError(error, "We couldn't load the reminder settings.")}
      </div>
    );
  }
  const dirty =
    JSON.stringify([...days].sort((a, b) => a - b)) !==
    JSON.stringify([...(data.days ?? [])].sort((a, b) => a - b));
  const run = (changes, success) =>
    save.mutate(changes, {
      onSuccess: () => {
        setProblem(null);
        toast.success(success);
      },
      onError: (err) => {
        setProblem(fieldProblems(err).days || null);
        toast.error(
          financeError(err, "We couldn't save the reminder settings.")
        );
      },
    });

  return (
    <section className={`${CARD} space-y-4 p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                data.enabled
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {data.enabled ? 'On' : 'Off'}
            </span>
            <p className="text-sm font-semibold text-gray-900">
              Monthly invoice reminders
            </p>
          </div>
          <p className="text-xs text-gray-500">
            {data.enabled
              ? `Consultants expected to invoice who haven’t are emailed at ${String(
                  data.hour ?? 9
                ).padStart(2, '0')}:00 Kenya time on these days.`
              : 'Off until you turn them on. Nothing is sent automatically while off.'}
            {data.updated_at &&
              ` Last changed ${formatDateTime(data.updated_at)}${
                data.updated_by ? ` by ${data.updated_by}` : ''
              }.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            run(
              { enabled: !data.enabled },
              data.enabled
                ? 'Monthly reminders are off.'
                : 'Monthly reminders are on.'
            )
          }
          disabled={save.isLoading || (!data.enabled && dirty)}
          className={data.enabled ? SECONDARY : PRIMARY}
        >
          {data.enabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>
      <div className="space-y-1">
        <p className="text-sm text-gray-700">Reminder days</p>
        <p className="text-xs text-gray-500">
          A day from the 15th on reminds about this month&apos;s invoice; days
          before the 15th remind about last month&apos;s, and the last of those
          is the final reminder. Each person gets each reminder once.
        </p>
      </div>
      <DaysEditor days={days} onChange={setDays} error={problem} />
      {dirty && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => run({ days }, 'Reminder days saved.')}
            disabled={save.isLoading}
            className={PRIMARY}
          >
            Save days
          </button>
          <button
            type="button"
            onClick={() => setDays(data.days)}
            className={SECONDARY}
          >
            Discard
          </button>
        </div>
      )}
    </section>
  );
}

const SOURCE_TEXT = {
  recent: 'Invoiced in the last 3 months',
  included: 'Included by finance',
  excluded: 'Excluded by finance',
  none: 'Not expected',
};

function ExpectedList() {
  const { data: people = [], isLoading, error } = useExpected();
  const setExpected = useSetExpected();
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');
  const shown = people.filter((p) =>
    `${p.name} ${p.email}`.toLowerCase().includes(q.trim().toLowerCase())
  );
  const change = (person, mode) => {
    setBusy(person.administrator_id);
    setExpected.mutate(
      { administratorId: person.administrator_id, mode },
      {
        onError: (err) =>
          toast.error(financeError(err, "We couldn't change that.")),
        onSettled: () => setBusy(null),
      }
    );
  };
  const expectedCount = people.filter((p) => p.expected).length;

  return (
    <section className={`${CARD} space-y-3 p-5`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            Expected to invoice ({expectedCount})
          </p>
          <p className="text-xs text-gray-500">
            Reminders only go to people expected to invoice: anyone who invoiced
            in the last 3 months, plus people you include. People you exclude
            are never reminded.
          </p>
        </div>
        <input
          type="search"
          aria-label="Search people"
          placeholder="Search name or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={`${INPUT} w-56`}
        />
      </div>
      {isLoading && (
        <div
          className="h-24 rounded bg-gray-100 animate-pulse"
          aria-label="Loading"
        />
      )}
      {error && (
        <p className="text-sm text-gray-600">
          {financeError(error, "We couldn't load the list.")}
        </p>
      )}
      <ul className="m-0 list-none divide-y divide-gray-100 p-0">
        {shown.map((p) => {
          const manual = p.source === 'included' || p.source === 'excluded';
          return (
            <li
              key={p.administrator_id}
              className="flex flex-wrap items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-900">
                  {p.name || p.email}{' '}
                  {p.name && (
                    <span className="text-xs text-gray-500">{p.email}</span>
                  )}
                </p>
                <p
                  className={`text-xs ${
                    p.expected ? 'text-green-700' : 'text-gray-500'
                  }`}
                >
                  {p.expected ? 'Expected · ' : ''}
                  {SOURCE_TEXT[p.source] || p.source}
                </p>
              </div>
              <div className="flex gap-2">
                {p.source !== 'included' && !p.expected && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => change(p, 'include')}
                    className={SECONDARY}
                  >
                    Include
                  </button>
                )}
                {p.source !== 'excluded' && p.expected && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => change(p, 'exclude')}
                    className={SECONDARY}
                  >
                    Exclude
                  </button>
                )}
                {manual && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => change(p, 'auto')}
                    className="text-xs text-claret-500 hover:underline disabled:opacity-50"
                  >
                    Reset to auto
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function FinanceReminders() {
  return (
    <div className="space-y-4">
      <Settings />
      <ExpectedList />
    </div>
  );
}
